/**
 * Tech-debt #9 — shared transport runtime + repo singletons for the station
 * store, extracted from useStationStore.ts as the first decomposition step.
 *
 * `runtimes` keys a per-store-instance `tag` object to its live transport
 * stack (a WeakMap so a discarded store instance is GC'd). The repo singletons
 * are constructed against the same `db` the rest of the app uses, so a
 * fresh-tab refresh hydrates from the same Dexie state.
 */
import type { AuthCache } from '@/domain/AuthCache';
import { ConnectorStatus, type StationId } from '@/domain/types';
import type { TxSample } from '@/lib/txTelemetry';
import type {
  CallDispatcher,
  OcppWebSocketTransport,
  ReconnectStrategy,
  WebSocketFactory,
} from '@/protocol';
import type { OcppAdapter } from '@/protocol/OcppAdapter';
import { db } from '@/persistence/dexie/db';
import {
  ConfigurationRepositoryDexie,
  ConnectorStateRepositoryDexie,
  LocalAuthListRepositoryDexie,
  StationRepositoryDexie,
  TopologyRepositoryDexie,
  TransactionRepositoryDexie,
  TransactionSamplesRepositoryDexie,
  VehicleRepositoryDexie,
} from '@/persistence/dexie/repositories';

export interface InternalRuntime {
  transport: OcppWebSocketTransport;
  dispatcher: CallDispatcher;
  adapter: OcppAdapter;
  factory: WebSocketFactory | undefined;
  /**
   * Session 13 — exponential-backoff retry for transient WS drops. Reset on a
   * successful Accepted boot; skipped on user-initiated disconnect
   * (the `userClosed` flag below).
   */
  reconnectStrategy: ReconnectStrategy;
  /** True after a successful boot (so a subsequent close is "transient"). */
  bootSucceeded: boolean;
  /** True after the user clicks Disconnect — suppresses auto-reconnect. */
  userClosed: boolean;
  /**
   * Session 8f.15 / BUG-028 — per-station OCPP 1.6J AuthCache (§9.2). Caches
   * CSMS Authorize responses so the next Authorize for the same idTag
   * short-circuits when `AuthorizationCacheEnabled` is 'true'. Survives
   * transport reconnects (only `init()` re-creates it); `clearAuthCache()`
   * wipes every entry.
   */
  authCache: AuthCache;
}

/**
 * BUG-028 / Session 8f.15 — AuthCache TTL. OCPP 1.6J does not mandate a value
 * (it's CP-implementation-defined); 24 h matches typical real-CP defaults and
 * is short enough that stale acceptances don't outlive a normal QA session.
 */
export const AUTH_CACHE_TTL_MS = 24 * 60 * 60 * 1_000;

export const runtimes = new WeakMap<object, InternalRuntime>();

// Session 12 §4.1 — repo singletons used by the store actions. CSMS-side repos
// (localAuthListVersionRepo, chargingProfileRepo) live in csms/shared.ts.
export const stationRepo = new StationRepositoryDexie(db);
export const transactionRepo = new TransactionRepositoryDexie(db);
export const configRepo = new ConfigurationRepositoryDexie(db);
export const topologyRepo = new TopologyRepositoryDexie(db);
export const localAuthListRepo = new LocalAuthListRepositoryDexie(db);
export const vehicleRepo = new VehicleRepositoryDexie(db);
export const connectorStateRepo = new ConnectorStateRepositoryDexie(db);
export const transactionSamplesRepo = new TransactionSamplesRepositoryDexie(db);

/**
 * TG-B — in-memory per-transaction telemetry buffers (power/SoC/energy),
 * filled by the SoC tick and persisted on StopTransaction. Keyed by
 * `${stationId}:${transactionId}` so multiple stations / connectors don't
 * collide. Cleared on stop and on soft/full reset.
 */
export const txSampleBuffers = new Map<string, readonly TxSample[]>();

export function txBufferKey(stationId: StationId, transactionId: number): string {
  return `${stationId}:${transactionId}`;
}

/**
 * BUG-022 — connector states that represent "EV connected and session live".
 * Moving OUT of this set while a tx is open triggers the auto-StopTransaction
 * (EVDisconnected) path, gated by the `StopTransactionOnEVSideDisconnect` key.
 */
export const CHARGING_STATES: ReadonlySet<ConnectorStatus> = new Set([
  ConnectorStatus.Charging,
  ConnectorStatus.SuspendedEV,
  ConnectorStatus.SuspendedEVSE,
]);
